import { tr } from "@/i18n/copy";
import { flushSync } from "react-dom";
import i18n from "@/i18n";
import { toast } from "sonner";
import { WorkflowGuideScope } from "@/components/WorkflowGuide";
import { createContext, createElement, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, errMsg, publicApi, setAccessToken, setDeviceSessionToken, beginSessionChange } from "@/lib/api";
import { applyLanguage, sessionLanguage, watchDeviceLanguage, currentPreference } from "@/i18n/language";
import { fetchWorkspaceBootstrap, watchPushNotifications, TENANT_WORKSPACE_ROLES } from "@/pwa";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, studio: null, entitlements: null, modules: [], workspace: null });
  const [status, setStatus] = useState("loading"); // loading | authed | guest
  useEffect(() => {
    if (status !== "authed" || !(TENANT_WORKSPACE_ROLES.has(state.user?.role) || state.user?.role === "platform_admin")) return;
    return watchPushNotifications(api, state.user.id);
  }, [status, state.user?.id, state.user?.role, state.user?.studio_id]);
  const statusRef = useRef(status);
  statusRef.current = status;
  const authVersion = useRef(0);
  const meInFlight = useRef(null);
  const authPending = useRef(false);
  const bootstrapRetry = useRef({ attempts: 0, nextAt: 0 });
  const lastMeAttempt = useRef(0);
  useEffect(() => watchDeviceLanguage(() => toast.error(i18n.t("lang.loadError"))), []);

  useEffect(() => {
    if (status !== "authed" || !state.user?.id) return;
    let active = true;
    let lastSynced = null;
    const sync = () => {
      const language = i18n.resolvedLanguage || i18n.language;
      if (!active || currentPreference() !== "auto" || lastSynced === language) return;
      lastSynced = language;
      api.put("/me/language/resolved", { language, expected_user_id: state.user.id })
        .catch(() => { if (active && lastSynced === language) lastSynced = null; });
    };
    sync();
    i18n.on("languageChanged", sync);
    window.addEventListener("online", sync);
    return () => { active = false; i18n.off("languageChanged", sync); window.removeEventListener("online", sync); };
  }, [status, state.user?.id]);

  const applyPayload = async (data, version) => {
    // P2.3 remains the single frontend bootstrap for tenant role, capability
    // and artist scope. platform_admin intentionally has no tenant workspace.
    if (version !== authVersion.current) return false;
    try { await applyLanguage(sessionLanguage(data.user, data.studio), () => version === authVersion.current); }
    catch { if (version === authVersion.current) toast.error(i18n.t("lang.loadError")); }
    if (version !== authVersion.current) return false;
    const workspace = await fetchWorkspaceBootstrap(api, data.user?.role, { timeout: 15000 });
    if (version !== authVersion.current) return false;
    setState({
      user: data.user,
      studio: data.studio,
      entitlements: data.entitlements,
      modules: data.modules || [],
      studios: data.studios || [],
      organizations: data.organizations || [],
      workspace,
    });
    setStatus("authed");
    bootstrapRetry.current = { attempts: 0, nextAt: 0 };
    return true;
  };

  const loadMe = useCallback(() => {
    if (authPending.current) return Promise.resolve();
    const version = authVersion.current;
    if (meInFlight.current?.version === version) return meInFlight.current.promise;
    const promise = (async () => {
      try {
        lastMeAttempt.current = Date.now();
        const data = await api.get("/auth/me", { timeout: 15000 });
        if (version !== authVersion.current) return;
        if (data?.access_token) setAccessToken(data.access_token);
        await applyPayload(data, version);
      } catch (error) {
        if (version !== authVersion.current) return;
        const code = error?.response?.status;
        if (code === 401 || code === 403) setStatus("guest");
        else if (statusRef.current === "loading") {
          const attempts = bootstrapRetry.current.attempts + 1;
          bootstrapRetry.current = { attempts, nextAt: Date.now() + [5000, 15000, 30000][Math.min(attempts - 1, 2)] };
          setState(current => ({ ...current, bootstrapError: true }));
        }
        // Network/5xx errors are recoverable and must not masquerade as logout.
        throw error;
      }
    })().finally(() => {
      if (meInFlight.current?.promise === promise) meInFlight.current = null;
    });
    meInFlight.current = { version, promise };
    return promise;
  }, []);

  useEffect(() => {
    // Team/Client invitation pages are deliberately public one-time flows.
    // They must not trigger auth bootstrap/refresh traffic in parallel with the
    // token preview or acceptance request, especially on Safari/iOS where a
    // failed third-party cookie refresh can surface as an unrelated network
    // failure. The invitation pages use the isolated public API client.
    const pathname = window.location.pathname || "";
    if (["/headquarters", "/brand-handover", "/invite/team", "/invite/client", "/forgot-password", "/reset-password"].includes(pathname)) {
      setStatus("guest");
      return;
    }
    loadMe().catch(() => {});
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const retry = bootstrapRetry.current;
      if ((statusRef.current === "authed" && Date.now() - lastMeAttempt.current >= 60000) ||
          (statusRef.current === "loading" && retry.attempts > 0 && retry.attempts <= 3 && Date.now() >= retry.nextAt)) loadMe().catch(() => {});
    }, 5000);
    const recover = () => { if (document.visibilityState === "visible") loadMe().catch(() => {}); };
    window.addEventListener("online", recover);
    document.addEventListener("visibilitychange", recover);
    window.addEventListener("pageshow", recover);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("online", recover);
      document.removeEventListener("visibilitychange", recover);
      window.removeEventListener("pageshow", recover);
    };
  }, [loadMe]);

  useEffect(() => {
    if (status !== "authed" || state.user?.role !== "artist") return;
    const changed = event => {
      const detail = event.detail || {};
      const types = detail.event_types || [detail.event_type].filter(Boolean);
      if (!types.length || detail.resync || types.includes("artist.updated")) loadMe().catch(() => {});
    };
    window.addEventListener("tps:data-changed", changed);
    return () => window.removeEventListener("tps:data-changed", changed);
  }, [status, state.user?.role, loadMe]);

  useEffect(() => {
    const expireSession = () => {
      authPending.current = false;
      authVersion.current += 1;
      setAccessToken(null);
      setDeviceSessionToken(null);
      setState({ user: null, studio: null, entitlements: null, modules: [], workspace: null });
      setStatus("guest");
    };
    window.addEventListener("tattoo-auth-expired", expireSession);
    return () => window.removeEventListener("tattoo-auth-expired", expireSession);
  }, []);

  const establishSession = async (request) => {
    const version = ++authVersion.current;
    beginSessionChange();
    authPending.current = true;
    try {
      const data = await request();
      if (version !== authVersion.current) throw new Error(tr("L'accesso è cambiato. Riprova dalla sessione corrente."));
      // Persist the Bearer token BEFORE changing auth state. This prevents the
      // dashboard's first API request from racing ahead on Safari/iPad.
      if (data?.access_token) setAccessToken(data.access_token);
      if (data?.device_session_token) setDeviceSessionToken(data.device_session_token);
      if (!await applyPayload(data, version)) throw new Error(tr("L'accesso è cambiato. Riprova dalla sessione corrente."));
      return data;
    } finally { if (version === authVersion.current) authPending.current = false; }
  };

  const login = (email, password, studio_id, client_studio_slug) => {
    // One unpredictable ID per user action, preserved by the transport retry.
    // Old/non-secure browsers without UUID support keep a single attempt.
    const login_request_id = globalThis.crypto?.randomUUID?.();
    const payload = { email, password, studio_id, client_studio_slug, login_request_id };
    return establishSession(async () => {
      if (!login_request_id) return api.post("/auth/login", payload, { timeout: 20000 });
      try {
        return await api.post("/auth/login-once", payload, { retryPasswordLogin: true, timeout: 20000 });
      } catch (error) {
        // During a rolling deploy an older API has no dedicated replay-safe
        // route. Only its explicit route-not-found response allows one legacy
        // login, without retries; never replay a POST on an older server.
        if (error?.response?.status === 404 && error?.response?.data?.detail === "Not Found") {
          return api.post("/auth/login", payload, { timeout: 20000 });
        }
        throw error;
      }
    });
  };
  const changeClientPassword = (relationshipId, payload) => establishSession(() => api.post(`/client/relationships/${relationshipId}/password`, payload));

  const acceptHandover = payload => establishSession(() => publicApi.post("/brand-handover/accept", payload));
  const acceptHeadquarters = payload => establishSession(() => publicApi.post("/headquarters/accept", payload));
  const joinBrand = payload => establishSession(() => publicApi.post("/brand-auth/join", payload));
  const separateBrand = payload => establishSession(() => publicApi.post("/brand-auth/separate-existing", payload));
  const register = (payload) => establishSession(() => api.post("/auth/register", payload));

  const clientRegister = async (studioSlug, payload) => {
    // Registration is deliberately credential-free. A 401 here means the
    // supplied password does not prove the existing Client account; it must
    // reach the form unchanged rather than triggering the protected-route
    // refresh interceptor (which has no refresh cookie on a public page).
    return establishSession(() => publicApi.post(`/auth/public/studios/${studioSlug}/client-register`, payload));
  };

  const googleExchange = async (code, verifier) => {
    const data = await publicApi.post("/auth/google/exchange", { code, verifier });
    if (data.registration_required) return data;
    return establishSession(() => Promise.resolve(data));
  };
  const googleClientRegister = (slug, registration_token, customer) => establishSession(() => publicApi.post(`/auth/google/public/studios/${slug}/register`, { registration_token, customer }));

  const googleSession = async (session_id) => {
    return establishSession(() => api.post("/auth/google/session", { session_id }));
  };

  const switchStudio = async (studioId) => {
    if (authPending.current) return false;
    const version = ++authVersion.current;
    beginSessionChange();
    authPending.current = true;
    bootstrapRetry.current = { attempts: 0, nextAt: 0 };
    statusRef.current = "loading";
    // The caller has already confirmed any drafts. Unmount the old workspace
    // (including its beforeunload guard) before changing server credentials.
    flushSync(() => {
      setState(current => ({ ...current, bootstrapError: false }));
      setStatus("loading");
    });
    try {
      const data = await api.post("/auth/switch-studio", { studio_id: studioId }, { timeout: 20000 });
      if (version !== authVersion.current) return false;
      setAccessToken(data.access_token);
      setDeviceSessionToken(data.device_session_token);
      // Keep the old workspace unmounted until a fresh document bootstraps.
      window.location.assign("/app");
      return true;
    } catch (error) {
      if (version !== authVersion.current) return false;
      authPending.current = false;
      // A failed transport may have reached the server. Re-read the canonical
      // session rather than putting the previous Studio's UI back blindly.
      await loadMe().catch(() => {});
      if (version === authVersion.current) toast.error(errMsg(error));
      return false;
    } finally { if (version === authVersion.current) authPending.current = false; }
  };

  const logout = async (options = {}) => {
    authPending.current = true;
    beginSessionChange();
    const version = ++authVersion.current;
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    if (version !== authVersion.current) return;
    authPending.current = false;
    setAccessToken(null);
    setDeviceSessionToken(null);
    setState({ user: null, studio: null, entitlements: null, modules: [], workspace: null,
      clientExit: state.user?.role === "client" ? options.clientExit : null });
    setStatus("guest");
  };

  const hasModule = (key) => {
    if (!state.entitlements) return false;
    if (state.user?.role === "platform_admin") return true;
    return (state.entitlements.modules || []).includes(key);
  };

  return (
    <AuthContext.Provider
      value={{ ...state, status, login, register, acceptHeadquarters, acceptHandover, separateBrand, joinBrand, clientRegister, changeClientPassword, googleExchange, googleClientRegister, googleSession, logout, switchStudio, loadMe, hasModule, errMsg }}
    >
      <WorkflowGuideScope userId={state.user?.id}>{children}</WorkflowGuideScope>
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);








// The owner may prepare their own canonical professional profile. This is a
// presentation scope only; server authorization still checks the real user.
export function OwnerProfessionalScope({children}) {
  const auth = useAuth(); const own = auth.workspace?.owner_professional;
  if (auth.user?.role !== "studio_owner" || !own) return children;
  const workspace = {...auth.workspace, role:"artist", owner_scope:true, artist_profile_id:own.id, service_types:own.service_types, primary_service_type:own.primary_service_type, activation_required:own.activation?.version === 2 && !own.activation?.activated_at};
  return createElement(AuthContext.Provider, {value:{...auth, user:{...auth.user,role:"artist"}, workspace}}, children);
}
